import { axiosRubyClient } from '@/util/axios/axiosClient';

// Read-only Actions Log feed over shift_events (internal/tenant-admin only).
// Backed by GET /api/admin/shift_events (+ /filters). See GRAVY-10196.

export type ShiftEventActorType = 'ops' | 'worker' | 'system';

export interface ShiftEventActor {
  type: ShiftEventActorType;
  id?: number | null;
  name?: string | null;
}

export interface ShiftEventShift {
  id: number;
  start_at: string | null;
  end_at: string | null;
  job_id: number | null;
  order_id: number | null;
  client_name: string | null;
  // Job-derived details for the Operations log rows (any may be null).
  position: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  location: string | null; // "DC 20002" (state + ZIP)
  pay_rate: string | null; // "$32.45/hr"
  pay_rate_cents: number | null;
  slots_total: number | null;
  slots_filled: number | null;
}

// A display-ready old -> new value change (e.g. a pay-rate edit).
export interface ShiftEventChange {
  field: string; // "Pay rate"
  from: string | null; // "$32.45/hr"
  to: string | null; // "$35.00/hr"
}

// A worker referenced by an event row (hired at cancellation, invited, etc.).
export interface ShiftEventWorker {
  id: number;
  name: string | null;
}

// Backwards-compatible alias for the worker shape hired at cancellation time.
export type ShiftEventHiredWorker = ShiftEventWorker;

// Invitation context captured for a `shift.invitation.sent` event, so the
// Actions Log can show who was invited and how instead of a bare description.
export interface ShiftEventInvitation {
  invited_worker_count: number;
  invited_workers: ShiftEventWorker[];
  sent_by: number | null; // sender user id (also surfaced as the row actor)
  channel: string | null; // "sms" | "push" | ...
}

// Cancellation context captured for a `shift.cancelled` event, so the Actions
// Log reflects the state at cancel time rather than the current job state.
export interface ShiftEventCancellation {
  reason: string | null; // "Client cancelled"
  hours_before_shift: string | null; // "3h 30m before shift"
  hired_worker_count: number; // hired workers captured at cancellation time
  hired_workers: ShiftEventHiredWorker[];
}

// Expiration context captured for a `shift.invitation.expired` event, so the
// Actions Log can show whose invitation lapsed and the offer's expiry window.
// System-triggered, so the worker is surfaced here rather than as the row actor.
export interface ShiftEventExpiration {
  worker: ShiftEventWorker | null;
  expiry_window_minutes: number | null; // offer lifetime, e.g. 120 -> "2h"
}

// Hire context captured for a `shift.worker.hired` event, so the Actions Log can
// show who was hired and how (admin approval vs. worker self-service) instead of
// a bare "Worker hired" line. approved_by is null for self-service claim/offer
// paths (no admin approver); for admin hires it is also surfaced as the row actor.
export interface ShiftEventHire {
  worker: ShiftEventWorker | null;
  approval_type: string | null; // "claim" | "offer_accepted" | "manual" | "order"
  approved_by: ShiftEventWorker | null; // approving admin, null for self-service
}

// Reason context for the three vacancy events — shift.worker.dropped
// (drop_reason), shift.invitation.declined (decline_reason), and
// worker.removed_from_shift (removal_reason) — so the Actions Log can show who
// left and why instead of a bare "Worker dropped the shift" line. The worker is
// surfaced here because ops/system-triggered rows don't carry it as the actor.
export interface ShiftEventReason {
  worker: ShiftEventWorker | null;
  reason: string | null; // "sick" | "double-booked" | ...
}

// A causal reference to the drop/removal event a backfill re-invite followed,
// resolved server-side from the captured drop_event_id, so the Actions Log can
// render a visible link like "Following drop by <worker>". Null when nothing
// was linked or the referenced event no longer exists.
export interface ShiftEventDropReference {
  event_id: string;
  event_type: string; // "shift.worker.dropped" | "worker.removed_from_shift"
  worker: ShiftEventWorker | null;
}

// Backfill context captured for a `shift.backfill.invite_sent` event, so the
// Actions Log can show who was re-invited, which pool the re-invite drew from,
// and the drop/removal it followed instead of a bare description. The event is
// system-triggered (no human sender), so recipients surface here, not as the
// row actor.
export interface ShiftEventBackfill {
  recipient_worker_count: number;
  recipient_workers: ShiftEventWorker[];
  pool_source: string | null; // "original" | "expanded"
  following: ShiftEventDropReference | null;
}

// Slot-fill context captured for a `shift.backfill.slot_filled` event, so the
// Actions Log can show who filled the vacated slot, who departed, how long the
// slot stood open, and the drop/removal it followed instead of a bare "Backfill
// slot filled" line. The event is system-triggered and its payload names the two
// workers separately, so both surface here rather than on the row actor.
export interface ShiftEventBackfillFill {
  replacement_worker: ShiftEventWorker | null;
  original_worker: ShiftEventWorker | null;
  minutes_to_fill: number | null; // how long the slot stood open, e.g. 45 -> "45 min"
  following: ShiftEventDropReference | null;
}

// Check-in context captured for a `shift.worker.checked_in` event, so the
// Actions Log can show how the worker checked in, whether their location was
// GPS-verified, and how early/late they were relative to the scheduled start —
// instead of a bare "Worker checked in" line. Shared by all five check-in flows
// (worker app/QR/manual and the two ops overrides). The worker is surfaced here
// because ops-initiated flows put the admin (not the worker) on the actor line.
export interface ShiftEventCheckin {
  worker: ShiftEventWorker | null;
  check_in_method: string | null; // "app" | "manual_app" | "qr" | "ops_manual" | "ops_enter_hours"
  location_verified: boolean | null; // GPS check (false for ops overrides, null if unknown)
  minutes_relative_to_start: number | null; // <0 early, >0 late, relative to shift start
}

export interface ShiftEventEntry {
  id: number;
  event_id: string;
  event_type: string;
  triggered_by: string;
  created_at: string | null;
  description: string;
  actor: ShiftEventActor;
  shift: ShiftEventShift | null;
  changes: ShiftEventChange[];
  cancellation: ShiftEventCancellation | null;
  invitation: ShiftEventInvitation | null;
  expiration: ShiftEventExpiration | null;
  hire: ShiftEventHire | null;
  reason: ShiftEventReason | null;
  backfill: ShiftEventBackfill | null;
  backfill_fill: ShiftEventBackfillFill | null;
  checkin: ShiftEventCheckin | null;
  json_data: Record<string, unknown> | null;
}

export interface Pagination {
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface ShiftEventsResponse {
  success: boolean;
  data: ShiftEventEntry[];
  pagination: Pagination;
}

export interface ShiftEventFilterOption {
  id: number;
  name: string;
}

export interface ShiftEventFiltersResponse {
  success: boolean;
  data: {
    users: ShiftEventFilterOption[];
    clients: ShiftEventFilterOption[];
  };
}

export interface ShiftEventsParams {
  page?: number;
  per_page?: number;
  search?: string;
  user_id?: number | string;
  client_id?: number | string;
  date?: string;
}

export const fetchShiftEvents = async (
  params: ShiftEventsParams,
): Promise<ShiftEventsResponse> => {
  const response = await axiosRubyClient.get('/api/admin/shift_events', {
    params,
  });
  return response.data;
};

export const fetchShiftEventFilters =
  async (): Promise<ShiftEventFiltersResponse> => {
    const response = await axiosRubyClient.get(
      '/api/admin/shift_events/filters',
    );
    return response.data;
  };
